import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useBooking } from "../context/BookingContext";
import { useLang } from "../context/LanguageContext";

const HERO_IMG = "/listings/westwind-5.jpg";

const Hero = () => {
  const { openBooking } = useBooking();
  const { t } = useLang();
  const ref = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 16 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(x * 8);
    rx.set(-y * 8);
  };

  const lines = [
    t("hero.line1"),
    <>{t("hero.line2a")} <em className="italic text-crimson">{t("hero.line2b")}</em></>,
    t("hero.line3"),
  ];

  const stats = [
    ["786", t("hero.statCareer")],
    ["68", t("hero.statYear")],
    ["$447K", t("hero.statAvg")],
  ];

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { rx.set(0); ry.set(0); }}
      className="relative overflow-hidden pt-20"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -right-40 -top-40 h-[540px] w-[540px] rounded-full border border-gold/30" />
      <div className="pointer-events-none absolute -right-24 -top-24 h-[380px] w-[380px] rounded-full border border-gold/20" />

      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 pb-20 pt-14 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:pt-20">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow flex items-center gap-3"
          >
            <span className="inline-block h-px w-10 bg-gold" />
            {t("hero.eyebrow")}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-navy letterpress sm:text-5xl lg:text-6xl"
          >
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.7 }}
            className="mt-6 max-w-md text-base leading-relaxed text-slate-600"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/listings"
              data-testid="hero-browse-button"
              className="group flex items-center gap-2 bg-navy px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-crimson"
            >
              {t("hero.browse")}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              data-testid="hero-qualified-button"
              onClick={() => openBooking({ service: "qualification" })}
              className="border border-crimson px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-crimson transition-colors duration-300 hover:bg-crimson hover:text-paper"
            >
              {t("hero.qualified")}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4 border-t border-engraved pt-6"
          >
            {stats.map(([v, l]) => (
              <div key={l}>
                <p className="font-serif text-2xl font-bold text-navy">{v}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
          className="relative hidden lg:block"
        >
          <div className="engraved-frame relative bg-white p-3 shadow-lifted">
            <div className="relative overflow-hidden">
              <img src={HERO_IMG} alt="Las Vegas luxury residence at dusk" className="h-[520px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{t("hero.nowShowing")}</p>
                  <p className="mt-1 font-serif text-xl font-semibold text-paper">1355 Westwind Road</p>
                </div>
                <p className="bg-paper px-3 py-1.5 font-mono text-xs font-medium text-navy">$2,500,000</p>
              </div>
            </div>
          </div>
          <div className="absolute -left-8 top-10 hidden bg-crimson px-4 py-3 text-paper shadow-lifted xl:block">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/70">{t("hero.nowShowing")}</p>
            <p className="font-serif text-lg font-bold">7 BD · 9 BA · 4,566 SF</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
